import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Navigation, MapPin, X, Clock, User, Target, Car } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { routeTrackingService } from "@/services/routeTrackingService";

interface HostRouteViewerProps {
  onClose: () => void;
}

interface RouteTracking {
  id: string;
  guest_user_id: string;
  host_user_id: string;
  current_latitude: number;
  current_longitude: number;
  destination_latitude: number;
  destination_longitude: number;
  destination_address: string;
  total_distance_km: number | null;
  estimated_duration_minutes: number | null;
  status: string;
  started_at: string;
  guest_name?: string;
}

const HostRouteViewer = ({ onClose }: HostRouteViewerProps) => {
  const { user } = useAuth();
  const [activeRoutes, setActiveRoutes] = useState<RouteTracking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    const loadActiveRoutes = async () => {
      try {
        const routes = await routeTrackingService.getRouteTrackingAsHost(user.id);
        
        // Fetch guest names for each route
        const routesWithNames = await Promise.all(
          routes.map(async (route) => {
            try {
              const { data: profile, error } = await supabase
                .from('user_profiles')
                .select('display_name, first_name, last_name')
                .eq('user_id', route.guest_user_id)
                .single();

              if (!error && profile) {
                const guestName = profile.display_name || 
                  `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 
                  'Guest';
                return { ...route, guest_name: guestName };
              }
            } catch (error) {
              console.error('Error fetching guest name:', error);
            }
            return { ...route, guest_name: 'Guest' };
          })
        );

        setActiveRoutes(routesWithNames);
      } catch (error) {
        console.error('Error loading active routes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadActiveRoutes();

    // Set up real-time subscription for route updates
    const channel = supabase
      .channel('host-route-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'route_tracking',
          filter: `host_user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Route update received:', payload);
          loadActiveRoutes(); // Reload routes when changes occur
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const calculateCurrentDistance = (route: RouteTracking) => {
    return routeTrackingService.calculateDistance(
      route.current_latitude,
      route.current_longitude,
      route.destination_latitude,
      route.destination_longitude
    );
  };

  const calculateETA = (route: RouteTracking) => {
    const distance = calculateCurrentDistance(route);
    const eta = routeTrackingService.calculateETA(
      route.current_latitude,
      route.current_longitude,
      route.destination_latitude,
      route.destination_longitude
    );
    return eta;
  };

  const openInMaps = (route: RouteTracking) => {
    const url = `https://www.google.com/maps/dir/${route.current_latitude},${route.current_longitude}/${route.destination_latitude},${route.destination_longitude}`;
    window.open(url, '_blank');
  };

  const formatETA = (eta: Date) => {
    const now = new Date();
    const diff = eta.getTime() - now.getTime();
    const minutes = Math.round(diff / (1000 * 60));
    if (minutes <= 0) return "Arriving now";
    return `${minutes} min${minutes !== 1 ? 's' : ''}`;
  };

  const formatDistance = (dist: number) => {
    if (dist < 1) return `${Math.round(dist * 1000)}m away`;
    return `${dist.toFixed(1)}km away`;
  };

  const formatStartTime = (startTime: string) => {
    const start = new Date(startTime);
    const now = new Date();
    const diff = now.getTime() - start.getTime();
    const minutes = Math.round(diff / (1000 * 60));
    
    if (minutes < 1) return "Just started";
    if (minutes < 60) return `${minutes} min${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.round(minutes / 60);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <Card className="mt-4 bg-carbon/90 border-neon-red backdrop-blur-glass animate-fade-in">
        <CardContent className="p-6">
          <div className="text-center text-ice-white/70">Loading route tracking...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4 bg-carbon/90 border-neon-red backdrop-blur-glass animate-fade-in">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-ice-white">
            <Navigation className="w-5 h-5 text-neon-red" />
            Guest Route Tracking
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-ice-white/60 hover:text-ice-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {activeRoutes.length === 0 ? (
          <div className="p-4 text-center text-ice-white/70">
            <MapPin className="w-8 h-8 mx-auto mb-2 text-ice-white/50" />
            <div>No active route tracking</div>
            <div className="text-sm">Guests will appear here when they start sharing their route to you</div>
          </div>
        ) : (
          <div className="space-y-3">
            {activeRoutes.map((route) => {
              const distance = calculateCurrentDistance(route);
              const eta = calculateETA(route);
              
              return (
                <div
                  key={route.id}
                  className="p-4 bg-neon-red/10 border border-neon-red/30 rounded-lg space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-neon-red rounded-full animate-pulse"></div>
                      <span className="text-ice-white font-medium">{route.guest_name}</span>
                    </div>
                    <span className="text-xs text-ice-white/60">
                      Started {formatStartTime(route.started_at)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-ice-white/80">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      <span>{formatDistance(distance)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      <span>ETA: {formatETA(eta)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-3 h-3" />
                      <span className="truncate">To: {route.destination_address}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => openInMaps(route)}
                      size="sm"
                      variant="outline"
                      className="flex-1 border-neon-red/30 text-neon-red hover:bg-neon-red/10"
                    >
                      <Car className="w-3 h-3 mr-1" />
                      View on Maps
                    </Button>
                  </div>

                  {distance < 0.1 && (
                    <div className="p-2 bg-toxic-green/20 border border-toxic-green/30 rounded text-center">
                      <span className="text-toxic-green text-sm font-medium">
                        🎉 Guest has arrived!
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HostRouteViewer;